export type Debounced = (() => void) & { cancel: () => void };

/**
 * Like lodash `debounce` with `maxWait`, but `fn` only ever runs from the debounce's own timer, never synchronously
 * from inside a call. Calls made while the main thread is busy collapse into one later invocation.
 */
export function debounce(
    fn: () => void,
    wait: number,
    { maxWait }: { maxWait: number },
): Debounced {
    let timer: ReturnType<typeof setTimeout> | null = null;
    let firstPendingCallAt = 0;
    let lastCallAt = 0;

    const onTimer = () => {
        const now = performance.now();
        const quietAt = lastCallAt + wait;
        const maxWaitAt = firstPendingCallAt + maxWait;
        if (now < quietAt && now < maxWaitAt) {
            timer = setTimeout(onTimer, Math.min(quietAt, maxWaitAt) - now);
            return;
        }
        timer = null;
        fn();
    };

    const debounced = () => {
        lastCallAt = performance.now();
        if (timer) {
            return;
        }
        firstPendingCallAt = lastCallAt;
        timer = setTimeout(onTimer, wait);
    };

    debounced.cancel = () => {
        if (timer) {
            clearTimeout(timer);
            timer = null;
        }
    };

    return debounced;
}
